import React, { useState } from 'react';
import { App, Button, Card, Col, Descriptions, Dropdown, InputNumber, Row, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  FileDoneOutlined,
  MailOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { APPLICATION_NEXT, fmtDate, fmtDateTime, fmtMoney, recruitErrorMessage, stageLabel } from './shared/recruitUi';

const { Text, Paragraph } = Typography;

const ApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.application(id || ''),
    queryFn: () => hrmsService.getApplication(id!),
    enabled: Boolean(id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applications() });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applicationBoard() });
  };

  const move = async (status: string, extra: object = {}) => {
    setBusy(true);
    try {
      await hrmsService.updateApplicationStatus(id!, { status, ...extra });
      message.success(`Moved to ${stageLabel(status)}`);
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const saveScore = async () => {
    if (score == null) return;
    setBusy(true);
    try {
      await hrmsService.updateApplicationStatus(id!, { status: row.status, screening_score: score });
      message.success('Screening score saved');
      setScore(null);
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const setOwner = async (owner: string | null) => {
    try {
      await hrmsService.bulkApplicationOwner({ ids: [id!], owner_employee_id: owner });
      message.success(owner ? 'Owner assigned' : 'Owner cleared');
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  const sendEmail = async (key: string) => {
    try {
      const r = await hrmsService.sendApplicationEmail(id!, key);
      if (r?.status === 'Sent') message.success('Email sent');
      else message.warning(`Email not sent: ${r?.failed_reason || r?.status || 'unknown'}`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidate(row.candidate_id) });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Application" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Application" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const next = (APPLICATION_NEXT[row.status] || []).filter((s) => s !== 'Rejected');
  const canReject = (APPLICATION_NEXT[row.status] || []).includes('Rejected');
  const closed = ['Hired', 'Rejected', 'Withdrawn'].includes(row.status);
  const activeOffer = (row.offers || []).find((o: any) => ['Draft', 'PendingApproval', 'Sent', 'Accepted'].includes(o.status));
  const answers = row.questionnaire_json && typeof row.questionnaire_json === 'object' ? Object.values(row.questionnaire_json as Record<string, any>) : [];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Application"
          documentNumber={row.application_number}
          subtitle={
            <Text type="secondary">
              <a onClick={() => navigate(`/hr/candidates/${row.candidate_id}`)}>{row.candidate_name}</a> for{' '}
              <a onClick={() => navigate(`/hr/jobs/${row.job_posting_id}`)}>{row.job_title}</a>
            </Text>
          }
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{stageLabel(row.status)}</Tag>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/applications')}>
                Back
              </Button>
              {!closed && (
                <Button icon={<CalendarOutlined />} onClick={() => navigate(`/hr/interviews/new?application_id=${id}`)}>
                  Schedule interview
                </Button>
              )}
              {!closed && (
                <Button icon={<ExperimentOutlined />} onClick={() => navigate(`/hr/assessments/new?application_id=${id}`)}>
                  Assign assessment
                </Button>
              )}
              {!closed && !activeOffer && ['Interview', 'Offer'].includes(row.status) && (
                <Button type="primary" icon={<FileDoneOutlined />} onClick={() => navigate(`/hr/offers/new?application_id=${id}`)}>
                  Create offer
                </Button>
              )}
              {canReject && (
                <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectOpen(true)}>
                  Reject
                </Button>
              )}
              <Dropdown
                menu={{
                  items: [
                    ...next.map((s) => ({ key: `move:${s}`, label: `Move to ${stageLabel(s)}` })),
                    { type: 'divider' as const },
                    { key: 'mail:application_received', label: 'Email: application received', icon: <MailOutlined /> },
                    ...(row.status === 'Rejected'
                      ? [{ key: 'mail:application_rejected', label: 'Email: application rejected', icon: <MailOutlined /> }]
                      : []),
                  ],
                  onClick: ({ key }) => {
                    const [kind, val] = key.split(':');
                    if (kind === 'move') void move(val);
                    if (kind === 'mail') void sendEmail(val);
                  },
                }}
              >
                <Button icon={<MoreOutlined />} loading={busy}>
                  More
                </Button>
              </Dropdown>
            </Space>
          }
        />
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Tabs
            items={[
              {
                key: 'summary',
                label: 'Summary',
                children: (
                  <>
                    <Card size="small" style={{ marginBottom: 16 }}>
                      <Descriptions column={2} size="small">
                        <Descriptions.Item label="Candidate">
                          {row.candidate?.candidate_number} — {row.candidate_name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contact">{row.candidate?.email || row.candidate?.phone || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Job">
                          {row.job?.job_number} — {row.job_title}
                        </Descriptions.Item>
                        <Descriptions.Item label="Applied via">{row.applied_via}</Descriptions.Item>
                        <Descriptions.Item label="Applied on">{fmtDate(row.created_at)}</Descriptions.Item>
                        <Descriptions.Item label="Stage since">{fmtDate(row.stage_changed_at || row.created_at)}</Descriptions.Item>
                        <Descriptions.Item label="Screening score">{row.screening_score ?? '—'}</Descriptions.Item>
                        {row.status === 'Rejected' && (
                          <Descriptions.Item label="Rejection">
                            {row.rejection_reason_code || '—'}
                            {row.rejection_notes ? ` — ${row.rejection_notes}` : ''}
                          </Descriptions.Item>
                        )}
                        {row.hired_employee_id && (
                          <Descriptions.Item label="Hired">
                            <a onClick={() => navigate(`/hr/employees/${row.hired_employee_id}`)}>Open employee</a>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                    {answers.length > 0 && (
                      <Card size="small" title="Screening answers" style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small">
                          {answers.map((a: any, i: number) => (
                            <Descriptions.Item key={i} label={a.question}>
                              {a.answer == null || a.answer === '' ? '—' : String(a.answer)}
                            </Descriptions.Item>
                          ))}
                        </Descriptions>
                      </Card>
                    )}
                    {row.cover_letter && (
                      <Card size="small" title="Cover letter" style={{ marginBottom: 16 }}>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{row.cover_letter}</Paragraph>
                      </Card>
                    )}
                  </>
                ),
              },
              {
                key: 'interviews',
                label: `Interviews (${row.interviews?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.interviews || []}
                    columns={[
                      { title: 'Round', dataIndex: 'round_number', width: 70 },
                      { title: 'When', dataIndex: 'scheduled_at', render: (v, i: any) => <a onClick={() => navigate(`/hr/interviews/${i.id}`)}>{fmtDateTime(v)}</a> },
                      { title: 'Mode', dataIndex: 'mode', width: 100 },
                      { title: 'Score', dataIndex: 'overall_score', width: 80, render: (v) => (v != null ? `${Number(v).toFixed(1)}/5` : '—') },
                      { title: 'Recommendation', dataIndex: 'recommendation', width: 130 },
                      { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'assessments',
                label: `Assessments (${row.assessments?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.assessments || []}
                    columns={[
                      { title: 'Number', dataIndex: 'assessment_number', render: (v, a: any) => <a onClick={() => navigate(`/hr/assessments/${a.id}`)}>{v}</a> },
                      { title: 'Title', dataIndex: 'title', ellipsis: true },
                      { title: 'Score', dataIndex: 'percentage_score', width: 90, render: (v) => (v != null ? `${v}%` : '—') },
                      { title: 'Result', dataIndex: 'pass_fail', width: 90, render: (v) => (v ? <Tag color={v === 'Pass' ? 'green' : 'red'}>{v}</Tag> : '—') },
                      { title: 'Status', dataIndex: 'status', width: 110 },
                    ]}
                  />
                ),
              },
              {
                key: 'offers',
                label: `Offers (${row.offers?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.offers || []}
                    columns={[
                      { title: 'Offer', dataIndex: 'offer_number', render: (v, o: any) => <a onClick={() => navigate(`/hr/offers/${o.id}`)}>{v}</a> },
                      { title: 'Basic', key: 'b', render: (_, o: any) => fmtMoney(o.basic_salary, o.currency) },
                      { title: 'Join', dataIndex: 'join_date', width: 120, render: (v) => fmtDate(v) },
                      { title: 'Status', dataIndex: 'status', width: 130, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
                    ]}
                  />
                ),
              },
            ]}
          />
          <EntityAttachmentsCard entityType="HR_APP" entityId={row.id} title="CV & application documents" uploadHint="CV, certificates, portfolio, screening notes" />
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="Owner" style={{ marginBottom: 16 }}>
            <RecruitPeoplePicker style={{ width: '100%' }} value={row.owner_employee_id} onChange={(v) => setOwner((v as string) || null)} placeholder="Unassigned" />
          </Card>
          {!closed && (
            <Card size="small" title="Screening score" style={{ marginBottom: 16 }}>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  placeholder={row.screening_score != null ? String(row.screening_score) : '0–100'}
                  value={score ?? undefined}
                  onChange={(v) => setScore(v)}
                />
                <Button type="primary" disabled={score == null} loading={busy} onClick={saveScore}>
                  Save
                </Button>
              </Space.Compact>
            </Card>
          )}
          {activeOffer && (
            <Card size="small" title="Active offer">
              <a onClick={() => navigate(`/hr/offers/${activeOffer.id}`)}>{activeOffer.offer_number}</a>{' '}
              <Tag color={hrmsStatusColor(activeOffer.status)}>{stageLabel(activeOffer.status)}</Tag>
            </Card>
          )}
        </Col>
      </Row>

      <ReasonPrompt
        open={rejectOpen}
        title="Reject application"
        okText="Reject"
        appliesTo="Application"
        showNotify
        onCancel={() => setRejectOpen(false)}
        onSubmit={async (v) => {
          setRejectOpen(false);
          await move('Rejected', { rejection_reason_code: v.reason_code, rejection_notes: v.notes, notify_candidate: v.notify_candidate });
        }}
      />
    </WorkspaceLayout>
  );
};

export default ApplicationDetailPage;
